import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { usePulseQuestionsChartData } from '../../../api/hooks/usePulseQuestionsChartData';
import { sortBy } from '../../../utils/sortBy';
import { useMemo, useState } from 'react';
import { DateRangeFilter } from '../DateRangeFilter';
import { useTranslate } from 'ra-core';
import { MoonstarTitle } from '../../Moonstar/MoonstarTitle';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Spinner } from '../../../components/admin/spinner';

export const PulseChecksChart = () => {
  const translate = useTranslate();

  const now = useMemo(() => new Date(), []);

  const [after, setAfter] = useState(() => {
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    return thirtyDaysAgo.toISOString().split('T')[0]!;
  });

  const [before, setBefore] = useState(now.toISOString().split('T')[0]!);

  const analyticsLabel = translate('moonstar.analytics.name');
  const pulseQuestionsLabel = translate('resources.pulseQuestions.name', {
    count: 2,
  });

  const { data: questions, isPending } = usePulseQuestionsChartData({
    after: new Date(after).toISOString(),
    before: new Date(before).toISOString(),
  });

  const sortedQuestions = useMemo(() => {
    if (!questions) return [];

    const data = questions.map((q) => {
      const positive = q.answers.filter(
        (a) =>
          a.type === AnswerType.Agree || a.type === AnswerType.StronglyAgree
      );

      const negative = q.answers.filter(
        (a) =>
          a.type === AnswerType.Disagree ||
          a.type === AnswerType.StronglyDisagree
      );

      const neutral = q.answers.filter((a) => a.type === AnswerType.Undecided);

      const answers = [
        {
          type: AnswerType.StronglyAgree,
          count: positive.reduce((sum, answer) => sum + answer.count, 0),
        },
        {
          type: AnswerType.StronglyDisagree,
          count: negative.reduce((sum, answer) => sum + answer.count, 0),
        },
        {
          type: AnswerType.Undecided,
          count: neutral[0]?.count || 0,
        },
      ];

      return {
        id: q.id,
        text: q.text,
        total: q.total,
        answers: sortBy(
          answers.filter((a) => a.count > 0),
          (x) => x.type,
          'descending'
        ),
      };
    });

    return sortBy(
      data,
      (x) => (x.total > 0 ? 1 : 0),
      'descending',
      (x) => x.id,
      'ascending'
    );
  }, [questions]);

  return (
    <>
      <MoonstarTitle breadcrumb={[analyticsLabel, pulseQuestionsLabel]} />
      <Card className="mt-5 shadow-md">
        <CardHeader className="flex flex-wrap items-center justify-between gap-4">
          <CardTitle>
            {translate('moonstar.dashboard.pulseChecks.title')}
          </CardTitle>
          <div className="flex flex-wrap items-center justify-end gap-4">
            <Legend />
            <DateRangeFilter
              startDate={after}
              endDate={before}
              setStartDate={setAfter}
              setEndDate={setBefore}
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table aria-label={translate('moonstar.dashboard.pulseChecks.title')}>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {translate('moonstar.dashboard.pulseChecks.answerCount')}
                </TableHead>
                <TableHead>
                  {translate('moonstar.dashboard.pulseChecks.question')}
                </TableHead>
                <TableHead>
                  {translate('moonstar.dashboard.pulseChecks.answers')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center">
                    <span className="inline-flex items-center gap-2">
                      <Spinner size="small" />
                      {translate('moonstar.dashboard.fetchingData')}
                    </span>
                  </TableCell>
                </TableRow>
              ) : sortedQuestions.length ? (
                sortedQuestions.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.total}</TableCell>
                    <TableCell>{item.text}</TableCell>
                    <TableCell className="w-1/2">
                      <StackedBar
                        items={item.answers.map((answer) => ({
                          type: answer.type,
                          percentage:
                            item.total > 0
                              ? Math.round((answer.count / item.total) * 1000) /
                                10
                              : 0,
                        }))}
                        colors={answerColors}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center">
                    {translate('moonstar.dashboard.noDataForRange')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
};

const AnswerType = {
  StronglyDisagree: 1,
  Disagree: 2,
  Undecided: 3,
  Agree: 4,
  StronglyAgree: 5,
} as const;

const answerColors: Record<
  (typeof AnswerType)[keyof typeof AnswerType],
  string
> = {
  [AnswerType.StronglyDisagree]: 'var(--chart-4)',
  [AnswerType.Disagree]: 'color-mix(in oklch, var(--chart-4) 60%, white)',
  [AnswerType.Undecided]: 'var(--muted-foreground)',
  [AnswerType.Agree]: 'color-mix(in oklch, var(--chart-1) 60%, white)',
  [AnswerType.StronglyAgree]: 'var(--chart-1)',
};

const StackedBar = (props: {
  items: { type: number; percentage: number }[];
  colors: { [key: number]: string };
}) => {
  return (
    <div className="flex h-[30px] w-full overflow-hidden rounded-md">
      {props.items.map((item, index) => (
        <div
          key={index}
          className="flex h-full items-center justify-center text-xs font-bold text-white [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]"
          style={{
            width: `${item.percentage}%`,
            backgroundColor: props.colors[item.type],
          }}
        >
          {item.percentage}%
        </div>
      ))}
    </div>
  );
};

const Legend = () => {
  const translate = useTranslate();

  return (
    <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
      <LegendItem
        color={answerColors[AnswerType.StronglyAgree]}
        label={translate('moonstar.dashboard.pulseChecks.agree')}
      />
      <LegendItem
        color={answerColors[AnswerType.Undecided]}
        label={translate('moonstar.dashboard.pulseChecks.undecided')}
      />
      <LegendItem
        color={answerColors[AnswerType.StronglyDisagree]}
        label={translate('moonstar.dashboard.pulseChecks.disagree')}
      />
    </div>
  );
};

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-2">
    <span className="size-4" style={{ backgroundColor: color }} />
    {label}
  </span>
);
